import { useDefaultHighlights } from "../../constants/Table";
import { CustomRendererType } from "../../types/Table";
import { useTranslation } from "react-i18next";
import {
  MpActions,
  MpColumn,
  // MpColumnFormat,
  // MpFilterType,
  MpTableType,
} from "@mp-react/table";
import { useMemo } from "react";
import { useMe } from "../../state/Administrators";
import { useTableFilterTypes } from "../../utils/Table";
import { useLocalStorage } from "../../utils/LocalStorage";
import { StorageKeys } from "../../types/LocalStorage";

export const useBenefitsChoicesTableType = (): MpTableType => {
  const { canEditChoices } = useMe();
  const { t } = useTranslation();
  const highlights = useDefaultHighlights();
  const { keyWithSuffix } = useLocalStorage();

  const {
    getSearchSelectFilter,
    dateRangeFilter,
    getSelectFilter,
    numberRangeFilter,
  } = useTableFilterTypes();

  const fullNameFilter = useMemo(
    () => getSearchSelectFilter("getFullNames"),
    [getSearchSelectFilter]
  );

  const flexStatusFilter = useMemo(
    () =>
      getSelectFilter("flexStatus", [
        {
          value: "selected",
          label: t("status.selected"),
        },
        {
          value: "notSelected",
          label: t("status.not_Selected"),
        },
        {
          value: "declined",
          label: t("status.declined"),
        },
      ]),
    [getSelectFilter, t]
  );

  const implementationFilter = useMemo(
    () =>
      getSelectFilter("implementationStatus", [
        {
          value: "done",
          label: t("table.done"),
        },
        {
          value: "pending",
          label: t("table.pending"),
        },
      ]),
    [getSelectFilter, t]
  );

  const columns: MpColumn[] = useMemo(
    () => [
      {
        label: t("common.time_remaining"),
        key: "timeRemaining",
        render: CustomRendererType.timeRemaining as any,
        sort: false,
        width: 106,
        fixedHeadWidth: true,
      },
      {
        label: t("choices.employee"),
        key: "employeeName",
        filter: fullNameFilter,
        width: 229,
        fixedHeadWidth: true,
      },
      {
        label: t("common.investment"),
        key: "investment",
        sort: false,
        render: CustomRendererType.currency as any,
        width: 124,
        fixedHeadWidth: true,
        filter: numberRangeFilter,
      },
      {
        label: t("choices.deadline"),
        key: "votingEndDate",
        render: CustomRendererType.dateFormat as any,
        filter: dateRangeFilter,
        sort: false,
        width: 125,
        fixedHeadWidth: true,
      },
      {
        label: t("common.status"),
        key: "flexStatus",
        render: CustomRendererType.statusPill as any,
        filter: flexStatusFilter,
        sort: false,
        width: 155,
        fixedHeadWidth: true,
      },
      {
        label: t("choices.implementation"),
        key: "implementationStatus",
        render: CustomRendererType.implementation as any,
        action: {
          slug: "toggleImplementationDate",
        },
        filter: implementationFilter,
        sort: false,
        width: 166,
        fixedHeadWidth: true,
      },
    ],
    [
      t,
      fullNameFilter,
      numberRangeFilter,
      dateRangeFilter,
      flexStatusFilter,
      implementationFilter,
    ]
  );

  const bulkActions: MpActions = useMemo(
    () => [
      {
        slug: "done",
        label: `${t("actions.mark_implementation_as_done")}`,
      },
      {
        slug: "pending",
        label: `${t("actions.mark_implementation_as_pending")}`,
      },
      {
        slug: "statusSelected",
        label: t("actions.status_selected"),
      },
      {
        slug: "statusDeclined",
        label: t("actions.status_declined"),
      },
      {
        slug: "statusNotSelected",
        label: t("actions.status_not_selected"),
      },
      {
        slug: "sendReminders",
        label: t("actions.send_reminder"),
      },
    ],
    [t]
  );

  return {
    columns: columns,
    rowIdKey: "id",
    filterVariant: "head",
    bulkActions: bulkActions,
    disablePagination: true,
    totals: true,
    stickyTotals: true,
    enableGlobalActions: true,
    selection: canEditChoices ? "multiple" : undefined,
    highlights: highlights,
    storageIdentifier: keyWithSuffix(StorageKeys.benefitChoicesTable),
    cacheFilters: true,
    cacheSorting: true,
    clearColumnFilters: true,
  };
};
